import { AppForms, AppImages, I18N, Theme, useAppI18N, variantProvider } from '@/app'
import { Button, Icon, Image, Select, Text, TextInput, View } from '@/components'
import { analytics, APIClient } from '@/services'
import { formatOrganisationEmployees } from '@/utils'
import { PropsOf, TypeGuards, useForm, useValidate } from '@codeleap/common'
import { modal } from '@codeleap/modals'
import { AnyRecord } from '@codeleap/types'
import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react'
import { Organisation } from '../../types'
import { OrganisationLogo } from '../atoms/OrgLogo'

const AddOrgModal = modal<{ name: string }, { organisation: Organisation }>()
  .content(props => {
    const { create: createOrg } = APIClient.Organisations.organisationsManager.useCreate()
    const form = useForm(AppForms.registerOrganisationModal, { initialState: { name: props.name, website: '' }})
    const { t } = useAppI18N()

    const ref = useRef(null)

    const onAddOrganisation = async () => {
      const values = { ...form.values }
      logger.log('Registering organisation', values)

      try {
        const organisation = await createOrg({
          website: values.website,
          name: values.name,
        })

        props.request.resolve({ organisation })
      } catch (e) {
        logger.error('Error registering organisation', e)
      }
    }

    return (
      <>
        <TextInput debugName='Organisation name' {...form.register('name')} ref={ref} type='text' />
        <TextInput debugName='Organisation URL' {...form.register('website')} />

        <Button
          debugName='Register organisation'
          onPress={onAddOrganisation}
          disabled={!form.isValid}
          text={t('SearchOrganisations.registerModal.submit')}
        />
      </>
    )
  })
  .props({
    title: I18N.t('organisations.registerModal.title'),
    showClose: true,
    styles: { box: { width: `calc(100vw - ${Theme.spacing.value(4)}px)`, maxWidth: 350 }},
    variants: ['centered', 'boxPadding:4'],
  })

const Item = props => {
  const {
    data: { item: data },
    isSelected,
  } = props

  if (data.is_unknown) return null

  const employeeInfo = formatOrganisationEmployees(data)

  return (
    <View
      variants={['row', isSelected && 'bg:primary2', 'border-radius:small', 'alignCenter', 'padding:2']}
      css={styles.itemWrapper}
    >
      <OrganisationLogo logo={data.logo} size={'tiny'} />
      <View variants={['column', 'marginLeft:2']}>
        <Text text={data.name} variants={['h5']} />
        {employeeInfo && <Text text={employeeInfo} variants={['p4']} />}
        <Text text={data.website} variants={['p4']} />
      </View>
      <Icon
        debugName='Organisation Select Check'
        name='check'
        css={{
          visibility: isSelected ? 'visible' : 'hidden',
        }}
        variants={['iconSize:2', 'marginLeft:auto']}
      />
    </View>
  )
}

function NoSearchPlaceholder() {
  const { t } = useAppI18N()
  return (
    <View variants={['column', 'alignCenter']}>
      <Image source={AppImages.NoOrganisationSearched} css={styles.noSearchImage} />
      <Text variants={['textCenter']}>{t('organisations.searchPrompt')}</Text>
    </View>
  )
}

function NotFoundPlaceholder({ search }) {
  const { t } = useAppI18N()
  return (
    <View variants={['column', 'alignCenter']}>
      <Image source={AppImages.OrganisationNotFound} css={styles.noSearchImage} />
      <Text variants={['textCenter']} text={t('organisations.noResults', { organisationName: search })} />
    </View>
  )
}

function validateOrg(value: string) {
  return {
    valid: TypeGuards.isNumber(value),
    message: I18N.t('form.validations.required'),
  }
}

export const OrganisationSelect = forwardRef<HTMLInputElement, PropsOf<typeof Select>>((props, selectRef) => {
  const { onValueChange, value, ...selectProps } = props
  const { t } = useAppI18N()

  const isSearching = useRef(false)

  const [selectedOrg, setSelectedOrg] = useState<AnyRecord | PropsOf<typeof Select>['value']>(value ?? '')

  const [search, setSearch] = useState('')

  const ref = useRef(null)
  useImperativeHandle(selectRef, () => ref?.current)

  const hasSearch = !!search?.trim()

  const unknownOrg = APIClient.Organisations.useUnknownOrganisation()
  const unknownOrganisation = unknownOrg?.organisation

  const validate = useValidate(value as string, validateOrg)

  const onChange = (item: AnyRecord) => {
    if (!item?.id) return

    onValueChange(item.id)
    setSelectedOrg({
      label: item.name,
      value: item.id,
      item: item,
    })

    setTimeout(() => {
      validate.onInputBlurred()
    }, 100)
  }

  const onPressAddOrganisation = async () => {
    if (!!search?.trim?.()) {
      analytics.track('org_add', { source: 'search_results' })
      console.log('Requesting new organisation with name', search)
      const { organisation } = await AddOrgModal.request({ name: search.trim() })
      if (organisation?.id) onChange(organisation)
    }
  }

  const noSearch = <NoSearchPlaceholder />
  const noResults = <NotFoundPlaceholder search={search} />

  const Footer = useCallback(() => {
    if (!hasSearch) return null

    return (
      <View variants={['padding:2', 'fullWidth']}>
        <Button
          onPress={onPressAddOrganisation}
          debugName='Add organisation'
          text={t('organisations.add')}
          icon='plus'
          variants={['outline', 'fullWidth']}
        />
      </View>
    )
  }, [hasSearch, onPressAddOrganisation])

  if (!unknownOrg?.organisation?.id) return null

  const unknownOrgOption = {
    label: unknownOrg?.organisation.name,
    value: unknownOrg?.organisation.id,
    item: unknownOrg?.organisation,
  }

  const loadOptions = async (search: string) => {
    const orgs = await APIClient.Organisations.organisationsManager.options.listItems(30, 0, {
      name: search,
      show_unverified: true,
    })

    if (!search?.trim()) return []

    const results = orgs.results.map(i => ({
      label: i.name,
      value: i.id,
      item: i,
    }))

    return results
  }

  const isUnknown = value === unknownOrgOption.value

  const someOrgId = isUnknown ? undefined : value

  const onInputChange = value => {
    setSearch(value)
    if (!isSearching.current && value.trim().length >= 1) {
      isSearching.current = true
      analytics.track('org_search', { source_from: 'onboarding' })
    }
  }

  return (
    <View variants={['fullWidth', 'relative']}>
      <Select
        loadOptions={loadOptions}
        searchable
        renderItem={Item}
        variants={['fullWidth']}
        PlaceholderComponent={() => (hasSearch ? null : noSearch)}
        PlaceholderNoItemsComponent={() => (hasSearch ? noResults : noSearch)}
        inputValue={search}
        onInputChange={newValue => onInputChange(newValue)}
        debugName='Organisation Select'
        FooterComponent={Footer}
        onValueChange={value => onValueChange(value ?? '')}
        value={someOrgId}
        defaultValue={unknownOrganisation?.id as any}
        {...selectProps}
        selectedOption={selectedOrg as any}
        setSelectedOption={value => setSelectedOrg(value ?? '')}
        // @ts-ignore
        ref={ref}
        validate={null}
        onBlur={() => {
          validate.onInputBlurred()
          isSearching.current = false
        }}
        onFocus={validate.onInputFocused}
        _error={validate?.isValid || !!value ? '' : (validate.message as string)}
        placeholder={isUnknown ? unknownOrganisation.name : selectProps?.placeholder}
        components={{
          ClearIndicator: null,
          SingleValue: o => {
            if (isUnknown) {
              return <Text text={unknownOrganisation?.name} />
            }

            return <Text text={o.data.label} />
          },
        }}
        escapeClearsValue
        clearable
        styles={{ list: { minHeight: LIST_MIN_HEIGHT }}}
      />
    </View>
  )
})

// This is for the "Unknown organisation" text to always be visible
const LIST_MIN_HEIGHT = 200 + Theme.spacing.value(4)

const styles = variantProvider.createComponentStyle(
  theme => ({
    noSearchImage: {
      height: 120,
      aspectRatio: 1.125,
      ...theme.spacing.marginBottom(4),
    },
    itemWrapper: {
      '&:hover': {
        backgroundColor: theme.colors.primary1,
      },
      cursor: 'pointer',
    },
    inputHidden: {
      position: 'absolute',
      opacity: 0,
      pointerEvents: 'none',
      left: 0,
      top: 0,
      zIndex: -9999,
    },
  }),
  true,
)
